import React, { useState, useEffect } from 'react';
import TaskCard from './TaskCard';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import { getAuthToken } from '../../services/api';
import './vStyles/VolunteerTasks.css';

interface Task {
  id: number;
  title: string;
  description: string;
  project_id: number;
  project_name: string;
  required_skills: string[];
  location: string;
  due_date?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const VolunteerTasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [appliedTaskIds, setAppliedTaskIds] = useState<number[]>([]);
  const [joinedProjects, setJoinedProjects] = useState<number[]>([]);
  const [volunteerSkills, setVolunteerSkills] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'projects' | 'skills'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { volunteerId } = useAuth();

  useEffect(() => {
    const fetchVolunteerTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!volunteerId) {
          setError('Please log in to view tasks');
          setLoading(false);
          return;
        }

        console.log('Fetching tasks for volunteerId:', volunteerId);
        
        const token = getAuthToken();
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          'user-id': volunteerId.toString()
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        let tasksData: Task[] = [];
        let appliedIds: number[] = [];
        
        // STRATEGY 1: Try the main volunteer tasks endpoint
        try {
          console.log('Trying main volunteer tasks endpoint...');
          const response = await fetch(`${API_BASE_URL}/api/volunteer/tasks?volunteerId=${volunteerId}`, {
            headers
          });

          if (response.ok) {
            tasksData = await response.json() as Task[];
            console.log('✅ Successfully fetched tasks from volunteer endpoint:', tasksData);
          } else {
            console.log('❌ Volunteer tasks endpoint failed, trying fallback...');
            throw new Error(`Volunteer endpoint returned ${response.status}`);
          }
        } catch (volunteerError) {
          console.log('Volunteer endpoint failed, trying fallback strategies...');
          
          // STRATEGY 2: Try general tasks endpoint
          try {
            console.log('Trying general tasks endpoint...');
            const fallbackResponse = await fetch(`${API_BASE_URL}/api/tasks`, { headers });
            if (fallbackResponse.ok) {
              tasksData = await fallbackResponse.json() as Task[];
              console.log('✅ Successfully fetched tasks from general endpoint:', tasksData);
            } else {
              throw new Error(`General endpoint returned ${fallbackResponse.status}`);
            }
          } catch (generalError) {
            console.log('General endpoint failed, trying project-based tasks...');
            
            // STRATEGY 3: Fetch tasks from joined projects
            try {
              console.log('Trying project-based tasks approach...');
              const projectsResponse = await fetch(`${API_BASE_URL}/api/volunteer/${volunteerId}/joined-projects`, { headers });
              if (projectsResponse.ok) {
                const joinedProjects = await projectsResponse.json();
                const projectIds = joinedProjects.map((p: any) => p.id);
                console.log('Joined project IDs:', projectIds);
                
                // Fetch tasks for each joined project
                const projectTasksPromises = projectIds.map(async (projectId: number) => {
                  try {
                    const projectTasksResponse = await fetch(`${API_BASE_URL}/api/projects/${projectId}/tasks`, { headers });
                    if (projectTasksResponse.ok) {
                      return await projectTasksResponse.json() as Task[];
                    }
                    return [];
                  } catch {
                    return [];
                  }
                });
                
                const projectTasksArrays = await Promise.all(projectTasksPromises);
                tasksData = projectTasksArrays.flat();
                console.log('✅ Successfully fetched tasks from projects:', tasksData);
              } else {
                throw new Error('All endpoints failed');
              }
            } catch (projectError) {
              console.error('All task fetching strategies failed:', projectError);
              tasksData = []; // Empty array as final fallback
            }
          }
        }

        // Fetch applied tasks (separate from main tasks)
        try {
          console.log('Fetching applied tasks...');
          const appliedResponse = await fetch(`${API_BASE_URL}/api/volunteer/${volunteerId}/applied-tasks`, { headers });
          if (appliedResponse.ok) {
            appliedIds = await appliedResponse.json() as number[];
            console.log('✅ Applied task IDs:', appliedIds);
          } else {
            console.log('❌ Could not fetch applied tasks');
          }
        } catch (appliedError) {
          console.error('Error fetching applied tasks:', appliedError);
        }

        // Fetch joined projects for filtering
        try {
          const projectsResponse = await fetch(`${API_BASE_URL}/api/volunteer/${volunteerId}/joined-projects`, { headers });
          if (projectsResponse.ok) {
            const projects = await projectsResponse.json();
            setJoinedProjects(projects.map((p: any) => p.id));
          }
        } catch (projectError) {
          console.error('Error fetching joined projects:', projectError);
        }

        // Fetch volunteer skills
        try {
          const volunteerResponse = await fetch(`${API_BASE_URL}/api/volunteers/${volunteerId}`, { headers });
          if (volunteerResponse.ok) {
            const volunteer = await volunteerResponse.json();
            setVolunteerSkills(volunteer.skills || []);
          }
        } catch (volunteerError) {
          console.error('Error fetching volunteer skills:', volunteerError);
        }

        setAppliedTaskIds(appliedIds);
        setAllTasks(tasksData);
        setTasks(tasksData);
        
        if (tasksData.length === 0) {
          setError('No tasks available. Tasks will appear here once you join projects with available tasks.');
        }
        
      } catch (err) {
        console.error('Error in fetchVolunteerTasks:', err);
        setError('Unable to load tasks at the moment. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteerTasks();
  }, [volunteerId]);

  const handleTaskApply = (taskId: number) => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { ...task, applied: true } : task
      )
    );
    setAppliedTaskIds(prev => [...prev, taskId]);
  };

  const handleFilterChange = (newFilter: 'all' | 'projects' | 'skills') => {
    setFilter(newFilter);
    
    let filteredTasks = allTasks;
    
    if (newFilter === 'projects') {
      filteredTasks = allTasks.filter(task => joinedProjects.includes(task.project_id));
    } else if (newFilter === 'skills') {
      filteredTasks = allTasks.filter(task => 
        task.required_skills?.some(skill => 
          volunteerSkills.some(vs => 
            vs.toLowerCase().includes(skill.toLowerCase()) || 
            skill.toLowerCase().includes(vs.toLowerCase())
          )
        )
      );
    }
    
    setTasks(filteredTasks);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading your tasks...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Filter out applied tasks
  const availableTasks = tasks.filter(task => !appliedTaskIds.includes(task.id));

  return (
    <div className="volunteer-tasks-container">
      <div className="volunteer-tasks-header">
        <h2>Your Available Tasks</h2>
        <div className="tasks-filter">
          <button 
            className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => handleFilterChange('all')}
          >
            All Tasks
          </button>
          <button 
            className={filter === 'projects' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => handleFilterChange('projects')}
          >
            My Projects
          </button>
          <button 
            className={filter === 'skills' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => handleFilterChange('skills')}
          >
            My Skills
          </button>
        </div>
      </div>

      {availableTasks.length === 0 ? (
        <div className="no-tasks">
          <p>No tasks available {filter !== 'all' ? `matching your ${filter}` : 'at the moment'}.</p>
          {filter === 'projects' && (
            <p>
              <a href="/volunteer/projects" style={{color: '#007bff', textDecoration: 'underline'}}>
                Join a project
              </a> first to see its tasks!
            </p>
          )}
        </div>
      ) : (
        <div className="tasks-grid">
          {availableTasks.slice(0, 5).map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onApply={handleTaskApply}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VolunteerTasks;
